import { FC, useEffect, useRef, useState } from "react";
import { gql } from "@apollo/client";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@src/lib/utils/cn";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import CheckIcon from "@src/lib/icons/12px/checkmark-curved";
import { UserType } from "@src/types.generated";
import {
  featuresFor,
  ReleaseHeadlineRelease,
} from "@src/features/home/releaseHeadline/releases";
import { explodeFromCenter } from "@src/features/home/releaseHeadline/releaseConfetti";
import { useOptIntoNovemberReleaseMutation } from "./OptInPowerUp.generated";

// How long each feature takes to switch on, and how long the celebration
// plays before the page reloads with the features on.
const STEP_MS = 450;
const CELEBRATE_MS = 2600;

type Phase = "charging" | "done" | "failed";

// What "Opt my company in early" shows over the letter: each of the release's
// features powers up and switches on in turn while the opt-in saves, then it
// all explodes in confetti and the page reloads with the features on.
const OptInPowerUp: FC<{
  release: ReleaseHeadlineRelease;
  // Features the opt-in leaves alone (Newsfeed keeps its own setup).
  excludeSlugs: string[];
  onDone: () => void;
  onFailed: () => void;
  onBack: () => void;
  onRetry: () => void;
}> = ({ release, excludeSlugs, onDone, onFailed, onBack, onRetry }) => {
  const features = featuresFor(release, UserType.Admin).filter(
    (feature) => !excludeSlugs.includes(feature.slug),
  );
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [lit, setLit] = useState(reduceMotion ? features.length : 0);
  const [saved, setSaved] = useState(false);
  const [phase, setPhase] = useState<Phase>("charging");
  const [optIn] = useOptIntoNovemberReleaseMutation();

  const save = () => {
    setPhase("charging");
    setSaved(false);
    optIn()
      .then(({ data }) => {
        if (!data?.optIntoNovemberRelease.success) throw new Error("refused");
        setSaved(true);
      })
      .catch(() => {
        setPhase("failed");
        onFailed();
      });
  };

  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on mount
  }, []);

  // One feature switches on per step; the last waits for the save.
  useEffect(() => {
    if (phase !== "charging" || lit >= features.length - 1) return;
    const timer = setTimeout(() => setLit((count) => count + 1), STEP_MS);
    return () => clearTimeout(timer);
  }, [phase, lit, features.length]);

  useEffect(() => {
    if (phase !== "charging" || !saved || lit < features.length - 1) return;
    const timer = setTimeout(
      () => {
        setLit(features.length);
        setPhase("done");
      },
      reduceMotion ? 0 : STEP_MS,
    );
    return () => clearTimeout(timer);
  }, [phase, saved, lit, features.length, reduceMotion]);

  useEffect(() => {
    if (phase !== "done") return;
    const rect = ref.current?.getBoundingClientRect();
    const cancel = rect ? explodeFromCenter(rect) : () => undefined;
    const timer = setTimeout(onDone, CELEBRATE_MS);
    return () => {
      cancel();
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when done
  }, [phase]);

  const progress = features.length ? lit / features.length : 1;

  return (
    <div
      ref={ref}
      data-testid="release-opt-in-power-up"
      tabIndex={-1}
      className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-6 bg-bg-primary px-12 py-8 outline-none"
    >
      {/* Only the headline is announced, not every switch flipping */}
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center gap-2 text-center"
      >
        <Text variant="display" className="text-text-primary">
          {phase === "done"
            ? "You’re in!"
            : phase === "failed"
              ? "We couldn’t switch them on"
              : "Powering up November…"}
        </Text>
        <Text variant="body-description" className="text-text-secondary">
          {phase === "done"
            ? "You’re all set. Reloading…"
            : phase === "failed"
              ? "Nothing changed. Try again in a moment."
              : "Switching on every feature for your company"}
        </Text>
      </div>

      {/* The charge: fills as each feature switches on */}
      <div className="h-2 w-full max-w-md overflow-hidden rounded-full bg-bg-tertiary">
        <motion.div
          className={cn(
            "h-full rounded-full",
            phase === "failed" ? "bg-status-error-solid" : "bg-action-primary",
          )}
          initial={false}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: reduceMotion ? 0 : STEP_MS / 1000 }}
        />
      </div>

      <ul className="m-0 flex w-full max-w-md list-none flex-col gap-2 p-0">
        {features.map((feature, i) => {
          const on = i < lit;
          const Icon = feature.icon;
          return (
            <motion.li
              key={feature.slug}
              data-testid={`release-opt-in-feature-${feature.slug}`}
              data-on={on}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-3 py-2 transition-colors duration-300",
                on
                  ? "border-brand-border bg-brand-bg"
                  : "border-border-secondary bg-bg-primary",
              )}
              animate={on && !reduceMotion ? { scale: [1, 1.04, 1] } : {}}
              transition={{ duration: 0.35 }}
            >
              <span
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-lg transition-all duration-300",
                  on
                    ? "bg-action-primary text-action-primary-foreground shadow-default"
                    : "bg-bg-tertiary text-icon-tertiary",
                )}
              >
                <Icon />
              </span>
              <Text
                variant="compact-title"
                className={cn(
                  "flex-1 transition-colors duration-300",
                  on ? "text-text-primary" : "text-text-tertiary",
                )}
              >
                {feature.title}
              </Text>
              {/* A switch flipping on */}
              <span
                aria-hidden
                className={cn(
                  "flex h-5 w-9 items-center rounded-full p-0.5 transition-colors duration-300",
                  on ? "bg-action-primary" : "bg-bg-tertiary",
                )}
              >
                <motion.span
                  className="flex size-4 items-center justify-center rounded-full bg-bg-primary shadow-default"
                  initial={false}
                  animate={{ x: on ? 16 : 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                >
                  {on && <CheckIcon className="text-brand-text" />}
                </motion.span>
              </span>
              <span className="sr-only">{on ? "On" : "Off"}</span>
            </motion.li>
          );
        })}
      </ul>

      {phase === "failed" && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}>
            Back to the letter
          </Button>
          <Button
            onClick={() => {
              onRetry();
              save();
            }}
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
};

export const OPT_INTO_NOVEMBER_RELEASE = gql`
  mutation OptIntoNovemberRelease {
    optIntoNovemberRelease {
      success
      error {
        code
        message
      }
    }
  }
`;

export default OptInPowerUp;
